import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import ClearIcon from "@mui/icons-material/Clear";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import CustomerModel from "./popups/customerlist/CustomerModel";
import RecentBillModel from "./recentbills/RecentBillModel";
import SalesReturnSearchModal from "./popups/salesreturn/SalesReturnSearchModal";
import { getDecryptedToken } from "../../../utils/crypto/cryptoUtils";
import { useState, useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import subscriptionService, {
  SubscriptionData,
} from "../../../services/subscription/subscriptionService";
import TvIcon from "@mui/icons-material/Tv";
import PaymentIcon from "@mui/icons-material/Payment";
import PosModel from "./popups/posmodel/PosModel";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store/store";
import {
  clearCustomer,
  setSaleType,
  setSaleDate,
  clearSaleDate,
} from "../../../store/pos/posSlice";
import InstantItem from "./InstantItem";
import { openCustomerView } from "../utils/openCustomerView";
import { detectExternalScreen } from "../utils/useScreenManager";
import { toast } from "react-toastify";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplayIcon from "@mui/icons-material/Replay";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York",
  "¥": "Asia/Tokyo",
  "Rs.": "Asia/Colombo",
  "€": "Europe/Berlin",
  "£": "Europe/London",
  "₹": "Asia/Kolkata",
  A$: "Australia/Sydney",
  C$: "America/Toronto",
};

const shopName = getDecryptedToken("martName");
const name = getDecryptedToken("name");
const currency = getDecryptedToken("currency");
const designation = (getDecryptedToken("designation") || "").toLowerCase();

const canBackdate =
  designation.includes("admin") || designation.includes("manager");

const topBtnBase =
  "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] transition-all duration-150 select-none";

const topBtnIdle =
  "border-white/15 bg-white/[0.08] text-white/90 hover:border-white/30 hover:bg-white/[0.16] hover:text-white";

const topBtnAccent =
  "border-erp-gold/40 bg-erp-gold text-erp-navy shadow-[0_2px_8px_rgba(201,162,77,0.28)] hover:bg-[#d4ad5a]";

const topBtnActiveGold =
  "border-erp-gold bg-erp-gold text-erp-navy shadow-[0_2px_8px_rgba(201,162,77,0.28)]";

function TopAction({
  onClick,
  icon,
  label,
  active,
  title,
}: {
  onClick: () => void;
  icon: ReactNode;
  label: string;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || label}
      className={`${topBtnBase} ${active ? topBtnActiveGold : topBtnIdle}`}
    >
      <span className="inline-flex !text-[15px] opacity-90 [&_svg]:!text-[15px]">
        {icon}
      </span>
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

function PosTop({
  onEditRecentBill,
}: {
  onEditRecentBill?: (billNumber: string | null) => void;
}) {
  const [isMOdelOpen, setModelOpen] = useState<boolean>(false);
  const [isPosMOdelOpen, setPosModelOpen] = useState<boolean>(false);
  const [isRecentBillsOpen, setRecentBillsOpen] = useState<boolean>(false);
  const [isSalesReturnOpen, setSalesReturnOpen] = useState<boolean>(false);
  const [isAddItemOpen, setAddItemOpen] = useState<boolean>(false);
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionData | null>(null);
  const [subscriptionTimer, setSubscriptionTimer] = useState<string>("");

  const [isBackdatePickerOpen, setBackdatePickerOpen] =
    useState<boolean>(false);
  const [pickerValue, setPickerValue] = useState<Dayjs | null>(null);

  const dispatch = useDispatch();
  const { customerId, customerName, customerPhone, customerType, saleType } =
    useSelector((state: RootState) => state.pos.current);
  const saleDate = useSelector((state: RootState) => state.pos.saleDate);
  const navigate = useNavigate();

  const openModel = () => setModelOpen(true);
  const openPosModel = () => setPosModelOpen(true);
  const openRecentBills = () => setRecentBillsOpen(true);
  const openSalesReturn = () => setSalesReturnOpen(true);
  const openAddItem = () => setAddItemOpen(true);

  const isClose = () => {
    setModelOpen(false);
    setPosModelOpen(false);
    setRecentBillsOpen(false);
    setSalesReturnOpen(false);
    setAddItemOpen(false);
  };

  const handleClearCustomer = () => {
    dispatch(clearCustomer());
  };

  const handleSaleTypeToggle = (type: "cash" | "credit") => {
    dispatch(setSaleType(type));
  };

  const handleOpenCustomerView = async () => {
    try {
      const externalScreen = await detectExternalScreen();

      if (externalScreen) {
        openCustomerView(externalScreen);
      } else {
        const defaultScreen = {
          left: 0,
          top: 0,
          width: window.screen.width,
          height: window.screen.height,
          isPrimary: true,
        };
        openCustomerView(defaultScreen);
      }
    } catch (error) {
      console.error("Failed to open customer view:", error);
      toast.error(
        "Failed to open customer view. Please check browser permissions.",
      );
    }
  };

  const handleOpenBackdatePicker = () => {
    setPickerValue(dayjs().subtract(1, "day"));
    setBackdatePickerOpen(true);
  };

  const handleConfirmBackdate = () => {
    if (!pickerValue) return;
    const selected = pickerValue.toDate();
    if (selected >= new Date()) {
      toast.error("Please select a past date and time");
      return;
    }
    dispatch(setSaleDate(selected.toISOString()));
    setBackdatePickerOpen(false);
  };

  const handleCancelBackdate = () => {
    setBackdatePickerOpen(false);
  };

  const handleClearBackdate = () => {
    dispatch(clearSaleDate());
  };

  const formatBackdate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const [currentTime, setCurrentTime] = useState(new Date());

  const timeZone =
    currencyTimeZoneMap[currency as string] ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const data = await subscriptionService.getCurrentSubscription();
        setSubscriptionData(data);
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }
    };
    fetchSubscription();
  }, []);

  useEffect(() => {
    if (subscriptionData?.subscription) {
      const updateTimer = () => {
        const endDate = new Date(subscriptionData.subscription!.endDate);
        const timeRemaining =
          subscriptionService.calculateTimeRemaining(endDate);
        setSubscriptionTimer(
          subscriptionService.formatTimeRemaining(timeRemaining),
        );
      };

      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [subscriptionData]);

  const subscriptionUrgent =
    !!subscriptionData?.timeRemaining?.days &&
    subscriptionData.timeRemaining.days <= 3;

  const dateLabel = currentTime.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone,
  });

  const timeLabel = currentTime.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <>
        <header className="relative overflow-hidden border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy shadow-[0_8px_24px_rgba(11,28,44,0.28)]">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,162,77,0.14),transparent_42%),radial-gradient(ellipse_at_bottom_left,rgba(31,111,139,0.18),transparent_45%)]"
          />

          <div className="relative flex min-h-[52px] items-center justify-between gap-3 px-3 py-1.5 lg:px-4">
            {/* Left: identity + context */}
            <div className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden">
              <div className="flex min-w-0 shrink-0 items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-erp-gold shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
                  <StorefrontIcon className="!text-[20px]" />
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="m-0 truncate font-brand text-[15px] font-semibold leading-none tracking-wide text-white">
                      {shopName || "POS"}
                    </p>
                    <span className="hidden h-1.5 w-1.5 shrink-0 rounded-full bg-erp-gold sm:inline-block" />
                  </div>
                  <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[10px] font-medium tracking-[0.02em] text-white/65">
                    <AccountCircleIcon className="!text-[13px] text-white/55" />
                    <span className="truncate capitalize">{name || "Cashier"}</span>
                    <span className="text-white/30">·</span>
                    <span className="hidden whitespace-nowrap tabular-nums sm:inline">
                      {dateLabel}
                    </span>
                    <span className="whitespace-nowrap font-mono text-[10px] tabular-nums text-erp-gold-soft/90">
                      {timeLabel}
                    </span>
                  </div>
                </div>
              </div>

              {saleDate ? (
                <div className="hidden min-w-0 items-center gap-1.5 rounded-lg border border-erp-gold/40 bg-erp-gold/20 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-erp-gold-soft md:flex">
                  <HistoryIcon className="!text-[14px]" />
                  <span className="truncate">
                    Backdated · {formatBackdate(saleDate)}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearBackdate}
                    title="Clear backdated mode"
                    className="ml-0.5 inline-flex rounded p-0.5 text-erp-gold-soft/80 transition-colors hover:bg-black/15 hover:text-white"
                  >
                    <ClearIcon className="!text-[13px]" />
                  </button>
                </div>
              ) : null}

              {customerId ? (
                <div className="ml-1 flex min-w-0 max-w-[420px] items-center gap-2 rounded-xl border border-white/15 bg-white/[0.07] px-2.5 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-erp-accent/30 text-white">
                    <PersonIcon className="!text-[16px]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-[11px] font-bold capitalize leading-tight text-white">
                      {customerName}
                    </p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      {customerPhone ? (
                        <span className="truncate text-[10px] text-white/60">
                          {customerPhone}
                        </span>
                      ) : null}
                      {customerType ? (
                        <span className="rounded bg-white/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white/85">
                          {customerType}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex shrink-0 overflow-hidden rounded-md border border-white/20 bg-black/20 p-0.5">
                    <button
                      type="button"
                      onClick={() => handleSaleTypeToggle("cash")}
                      className={[
                        "rounded px-2 py-1 text-[9px] font-bold uppercase tracking-wide transition-colors",
                        saleType === "cash"
                          ? "bg-white text-erp-navy shadow-sm"
                          : "text-white/70 hover:text-white",
                      ].join(" ")}
                    >
                      Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaleTypeToggle("credit")}
                      className={[
                        "rounded px-2 py-1 text-[9px] font-bold uppercase tracking-wide transition-colors",
                        saleType === "credit"
                          ? "bg-erp-accent text-white shadow-sm"
                          : "text-white/70 hover:text-white",
                      ].join(" ")}
                    >
                      Credit
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleClearCustomer}
                    title="Clear Customer"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/55 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <ClearIcon className="!text-[15px]" />
                  </button>
                </div>
              ) : null}
            </div>

            {/* Right: actions */}
            <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TopAction
                onClick={() => navigate("/")}
                icon={<AccountBalanceIcon />}
                label="Dashboard"
              />
              <TopAction
                onClick={openPosModel}
                icon={<AttachMoneyIcon />}
                label="Cash"
              />
              <TopAction
                onClick={openModel}
                icon={<AddIcon />}
                label="Customer"
              />
              <TopAction
                onClick={openAddItem}
                icon={<InventoryIcon />}
                label="Item"
              />
              <TopAction
                onClick={openRecentBills}
                icon={<ReceiptLongIcon />}
                label="Bills"
              />
              <TopAction
                onClick={openSalesReturn}
                icon={<ReplayIcon />}
                label="Return"
              />
              <TopAction
                onClick={handleOpenCustomerView}
                icon={<TvIcon />}
                label="View"
              />
              {canBackdate ? (
                <TopAction
                  onClick={handleOpenBackdatePicker}
                  icon={<HistoryIcon />}
                  label="Backdate"
                  active={!!saleDate}
                  title="Enter a sale for a past date"
                />
              ) : null}

              <div className="ml-1 hidden items-center overflow-hidden rounded-xl border border-white/15 bg-black/20">
                {subscriptionTimer ? (
                  <div
                    className={[
                      "flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-bold tabular-nums",
                      subscriptionUrgent
                        ? "text-[#ff8f8f]"
                        : "text-erp-gold-soft",
                    ].join(" ")}
                  >
                    <AccessTimeIcon className="!text-[13px]" />
                    <span className="font-mono">{subscriptionTimer}</span>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    navigate("/shop/pay", { state: { subscriptionData } })
                  }
                  className={`${topBtnBase} ${topBtnAccent} rounded-none rounded-r-[11px] border-0`}
                >
                  <PaymentIcon className="!text-[15px]" />
                  <span>Pay</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {isBackdatePickerOpen ? (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-erp-navy/55 p-4 backdrop-blur-[2px]">
            <div className="w-full max-w-[380px] rounded-2xl border border-erp-line bg-white p-5 shadow-[0_24px_60px_rgba(11,28,44,0.28)]">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-erp-gold/15 text-erp-gold">
                  <HistoryIcon className="!text-[20px]" />
                </div>
                <div>
                  <h3 className="m-0 text-[15px] font-semibold text-erp-ink">
                    Backdated Sale
                  </h3>
                  <p className="m-0 text-[12px] text-erp-muted">
                    Choose a past date and time for this sale
                  </p>
                </div>
              </div>
              <p className="mb-4 mt-0 text-[12px] leading-relaxed text-erp-muted">
                This sale will appear under the selected date in reports and
                will be marked as backdated.
              </p>
              <DateTimePicker
                label="Sale Date & Time"
                value={pickerValue}
                onChange={(val) => setPickerValue(val)}
                disableFuture
                slotProps={{
                  textField: { fullWidth: true, size: "small" },
                }}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancelBackdate}
                  className="h-9 rounded-lg border border-erp-line bg-white px-4 text-[12px] font-semibold text-erp-muted transition-colors hover:border-erp-muted hover:text-erp-ink"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmBackdate}
                  className="h-9 rounded-lg bg-erp-gold px-4 text-[12px] font-bold text-erp-navy transition-colors hover:bg-[#d4ad5a]"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {isMOdelOpen && (
          <CustomerModel isCloseMain={isClose} isOpen={isMOdelOpen} />
        )}
        {isPosMOdelOpen && (
          <PosModel isCloseMain={isClose} isOpen={isPosMOdelOpen} />
        )}
        {isRecentBillsOpen && (
          <RecentBillModel
            isCloseMain={isClose}
            isOpen={isRecentBillsOpen}
            onEditBill={onEditRecentBill}
          />
        )}
        {isSalesReturnOpen && (
          <SalesReturnSearchModal
            isOpen={isSalesReturnOpen}
            onClose={() => setSalesReturnOpen(false)}
          />
        )}
        {isAddItemOpen && (
          <InstantItem setopen={setAddItemOpen} open={isAddItemOpen} />
        )}
      </>
    </LocalizationProvider>
  );
}

export default PosTop;
